'use client';

import type { PopoverContentProps, PopoverProps } from './Popover.types';
import styles from './Popover.module.css';
import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useMemo,
  useState,
  useId,
} from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { Flex } from '../Flex';
import { FlexProps } from '../Flex/Flex.types';

export function usePopover({
  inheritTriggerWidth = false,
  initialOpen = false,
  isDisabled = false,
  onOpenChange: setControlledOpen,
  open: controlledOpen,
  placement = 'top',
  role: passedRole = 'dialog',
}: PopoverProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const contentId = useId();
  const triggerId = useId();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
  });

  const context = data.context;

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: passedRole });

  const interactions = useInteractions([click, dismiss, role]);

  return useMemo(
    () => ({
      inheritTriggerWidth,
      isDisabled,
      open,
      setOpen,
      contentId,
      triggerId,
      ...interactions,
      ...data,
    }),
    [
      data,
      inheritTriggerWidth,
      interactions,
      isDisabled,
      open,
      setOpen,
      contentId,
      triggerId,
    ],
  );
}

type ContextType = ReturnType<typeof usePopover> | null;

const PopoverContext = createContext<ContextType>(null);

export const usePopoverContext = () => {
  const context = useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};

/**
 * Popover primitive UI component that wraps the trigger and content components.
 *
 * @example
 * <Popover placement="right">
 *   <Popover.Trigger>
 *     <button>Click here!</button>
 *   </Popover.Trigger>
 *   <Popover.Content>
 *     <Text type="small" color="currentcolor">
 *       Hello from the popover!
 *     </Text>
 *   </Popover.Content>
 * </Popover>
 */
export const Popover = ({
  children,
  ...options
}: { children: React.ReactNode } & PopoverProps) => {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = usePopover(options);
  return <PopoverContext value={popover}>{children}</PopoverContext>;
};

/**
 * Element to trigger the popover when clicked.
 *
 * Trigger element must be refable (e.g., a native HTML element, a functional
 * component that forwards its ref, etc.); if not, the trigger element will be
 * wrapped in a div.
 *
 * @example
 * <Popover placement="right">
 *   <Popover.Trigger>
 *     <button>Click here!</button>
 *   </Popover.Trigger>
 *   <Popover.Content>
 *     <Text type="small" color="currentcolor">
 *       Hello from the popover!
 *     </Text>
 *   </Popover.Content>
 * </Popover>
 */
Popover.Trigger = forwardRef<HTMLElement, React.HTMLProps<HTMLElement>>(
  function PopoverTrigger({ children }, propRef) {
    const context = usePopoverContext();

    const ref = useMergeRefs([context.refs.setReference, propRef]);

    if (children == null) return null; // avoid breaking UX for user in unexpected scenario

    const isChildRefable =
      isValidElement(children) &&
      'ref' in children &&
      (typeof children.type === 'string' || // child is native HTML element
        (typeof children.type === 'object' && // child is ForwardRefExoticComponent
          (children.type as any).$$typeof === Symbol.for('react.forward_ref')));

    if (!isValidElement(children)) {
      throw new Error('Popover.Trigger must have a single child element');
    }

    if (!isChildRefable) {
      return (
        <div
          ref={ref}
          data-state={context.open ? 'open' : 'closed'}
          id={context.triggerId}
          aria-controls={context.contentId}
          aria-expanded={context.open}
        >
          {children}
        </div>
      );
    }

    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        id: context.triggerId,
        'aria-controls': context.contentId,
        'aria-expanded': context.open,
        ...(children as any).props,
        // The user can style the trigger based on the state
        'data-state': context.open ? 'open' : 'closed',
      }),
    );
  },
);

/**
 * Content to be rendered inside the popover when it's shown. Can be text, or
 * any other element type.
 *
 * All {@link Flex} props are supported.
 *
 * @example
 * <Popover placement="right">
 *   <Popover.Trigger>
 *     <button>Click here!</button>
 *   </Popover.Trigger>
 *   <Popover.Content>
 *     <Text type="small" color="currentcolor">
 *       Hello from the popover!
 *     </Text>
 *   </Popover.Content>
 * </Popover>
 */
Popover.Content = forwardRef<
  HTMLDivElement,
  FlexProps & React.HTMLProps<HTMLDivElement> & PopoverContentProps
>(function PopoverContent({ style, ...props }, propRef) {
  const context = usePopoverContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  // Sensible defaults, allowing overriding if necessary
  const { padding = 6, maxWidth = '500px' } = props;
  props = {
    ...props,
    maxWidth,
    padding,
  };

  const { isMounted, styles: transitionStyles } = useTransitionStyles(
    context.context,
    {
      duration: 96,
    },
  );

  if (!isMounted || context.isDisabled) return null;

  const referenceWidth =
    context.refs.reference?.current instanceof HTMLElement
      ? context.refs.reference.current.getBoundingClientRect().width
      : undefined;

  return (
    <FloatingPortal>
      <Flex
        ref={ref}
        {...props}
        id={context.contentId}
        aria-labelledby={context.triggerId}
        className={styles.popoverContent}
        style={{
          ...context.floatingStyles,
          ...style,
          ...transitionStyles,
          maxHeight: props.maxHeight,
          maxWidth: context.inheritTriggerWidth ? undefined : props.maxWidth,
          width: context.inheritTriggerWidth ? referenceWidth : undefined,
        }}
        {...context.getFloatingProps(props)}
      >
        {props.children}
      </Flex>
    </FloatingPortal>
  );
});
